import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Star, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ParishCard Component
 *
 * Displays parish information in a card format with mass times.
 *
 * @param {Object} parish - Parish data object
 * @param {string} parish.id - Parish ID
 * @param {string} parish.name - Parish name
 * @param {string} [parish.address] - Street address
 * @param {string} [parish.city] - City name
 * @param {string} [parish.state] - State code
 * @param {string} [parish.zipCode] - ZIP code
 * @param {string} [parish.email] - Email address
 * @param {string} [parish.phoneNumber] - Phone number
 * @param {string} [parish.description] - Parish description
 * @param {string} [parish.imageUrl] - URL to the parish cover image
 * @param {Object} [parish.parishInfo] - Additional parish info (JSON)
 * @param {Array|Object} [parish.parishInfo.massTimes] - Mass times data
 * @param {number} [parish.distance] - Distance in miles (optional, for nearest parishes)
 * @param {boolean} [parish.isPrimary] - Whether this is the primary parish
 * @param {Function} [onClick] - Optional click handler
 * @param {Function} [onSetPrimary] - Optional handler to set this parish as primary
 * @param {boolean} [isSettingPrimary] - Whether the set primary action is in progress
 */
const ParishCard = ({ parish, onClick, onSetPrimary, isSettingPrimary = false }) => {
  const navigate = useNavigate();
  const {
    id,
    name,
    address,
    city,
    state,
    zipCode,
    email,
    phoneNumber,
    description,
    imageUrl,
    parishInfo,
    distance,
  } = parish;

  // Format address
  const formatAddress = () => {
    const parts = [address, city, state, zipCode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const MAX_CARD_DAYS = 2;

  // Format mass times (truncated for card view)
  const formatMassTimes = () => {
    if (!parishInfo?.massTimes) return null;

    const massTimes = parishInfo.massTimes;

    // Handle array format
    if (Array.isArray(massTimes)) {
      if (massTimes.length === 0) return null;
      // New grouped format: Array of { day, times: [{ time, note? }] }
      if (typeof massTimes[0] === 'object' && massTimes[0].day && Array.isArray(massTimes[0].times)) {
        const visible = massTimes.slice(0, MAX_CARD_DAYS);
        const remaining = massTimes.length - MAX_CARD_DAYS;
        return (
          <>
            {visible.map((dayGroup, index) => (
              <div key={index} className="text-sm text-charcoal-muted flex justify-between">
                <span className="font-medium text-charcoal">{dayGroup.day}:</span>
                <span>{dayGroup.times.map(s => s.time).join(', ')}</span>
              </div>
            ))}
            {remaining > 0 && (
              <div className="text-xs text-charcoal-subtle">+ {remaining} more {remaining === 1 ? 'day' : 'days'}</div>
            )}
          </>
        );
      }
      // Legacy flat format: Array of {day, time} objects - group by day
      if (typeof massTimes[0] === 'object' && massTimes[0].day) {
        const grouped = {};
        massTimes.forEach(entry => {
          if (!grouped[entry.day]) grouped[entry.day] = [];
          grouped[entry.day].push(entry.time);
        });
        const entries = Object.entries(grouped);
        const visible = entries.slice(0, MAX_CARD_DAYS);
        const remaining = entries.length - MAX_CARD_DAYS;
        return (
          <>
            {visible.map(([day, times]) => (
              <div key={day} className="text-sm text-charcoal-muted flex justify-between">
                <span className="font-medium text-charcoal">{day}:</span>
                <span>{times.join(', ')}</span>
              </div>
            ))}
            {remaining > 0 && (
              <div className="text-xs text-charcoal-subtle">+ {remaining} more {remaining === 1 ? 'day' : 'days'}</div>
            )}
          </>
        );
      }
      // Array of strings (legacy)
      const visible = massTimes.slice(0, 3);
      const remaining = massTimes.length - 3;
      return (
        <>
          {visible.map((time, index) => (
            <div key={index} className="text-sm text-charcoal-muted">
              {typeof time === 'string' ? time : JSON.stringify(time)}
            </div>
          ))}
          {remaining > 0 && (
            <div className="text-xs text-charcoal-subtle">+ {remaining} more</div>
          )}
        </>
      );
    }

    // Handle object format (e.g., { "Sunday": "9:00 AM", "Monday": "7:00 AM" })
    if (typeof massTimes === 'object') {
      const entries = Object.entries(massTimes);
      const visible = entries.slice(0, MAX_CARD_DAYS);
      const remaining = entries.length - MAX_CARD_DAYS;
      return (
        <>
          {visible.map(([day, time]) => (
            <div key={day} className="text-sm text-charcoal-muted flex justify-between">
              <span className="font-medium">{day}:</span>
              <span>{time}</span>
            </div>
          ))}
          {remaining > 0 && (
            <div className="text-xs text-charcoal-subtle">+ {remaining} more {remaining === 1 ? 'day' : 'days'}</div>
          )}
        </>
      );
    }

    // Handle string format
    if (typeof massTimes === 'string') {
      return (
        <div className="text-sm text-charcoal-muted">
          {massTimes}
        </div>
      );
    }

    return null;
  };

  const massTimesContent = formatMassTimes();
  const hasMassTimes = massTimesContent !== null;
  const isPrimary = parish.isPrimary || false;

  const handleCardClick = (e) => {
    // Don't trigger card click if clicking on button
    if (e.target.closest('button')) {
      return;
    }
    if (onClick) {
      onClick();
    } else {
      // Default behavior: navigate to parish detail page
      navigate(`/app/donor/parish/${id}`);
    }
  };

  const handleSetPrimaryClick = (e) => {
    e.stopPropagation();
    if (onSetPrimary) {
      onSetPrimary();
    }
  };

  return (
    <div
      className={`
        bg-white border border-surface-border
        transition-all duration-200
        h-full flex flex-col relative overflow-hidden
        cursor-pointer hover:border-gold/30 hover:shadow-subtle
      `}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
    >
      {/* Cover Image */}
      {imageUrl && (
        <div className="relative w-full h-48 flex-shrink-0 overflow-hidden">
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Hide image on error
              e.target.style.display = 'none';
            }}
          />
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Primary Badge - Over image */}
          {isPrimary && (
            <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-gold/15 backdrop-blur-sm px-2 py-0.5 rounded-sm">
              <Star className="w-3.5 h-3.5 text-gold fill-gold" />
              <span className="text-xs text-gold font-semibold tracking-wide">Primary</span>
            </div>
          )}

          {/* Set as Primary Button - Over image */}
          {onSetPrimary && !isPrimary && (
            <div className="absolute top-4 right-4 z-10">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSetPrimaryClick}
                disabled={isSettingPrimary}
                className="bg-white/90 backdrop-blur-sm text-charcoal-muted hover:text-charcoal border-charcoal/20"
              >
                {isSettingPrimary ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-1" />
                    Set as Primary
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Primary Badge - When no image */}
      {!imageUrl && isPrimary && (
        <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-gold/15 px-2 py-0.5 rounded-sm">
          <Star className="w-3.5 h-3.5 text-gold fill-gold" />
          <span className="text-xs text-gold font-semibold tracking-wide">Primary</span>
        </div>
      )}

      {/* Set as Primary Button - When no image */}
      {!imageUrl && onSetPrimary && !isPrimary && (
        <div className="absolute top-4 right-4 z-10">
          <Button
            size="sm"
            variant="outline"
            onClick={handleSetPrimaryClick}
            disabled={isSettingPrimary}
            className="bg-white/90 text-charcoal-muted hover:text-charcoal border-charcoal/20"
          >
            {isSettingPrimary ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Star className="w-4 h-4 mr-1" />
                Set as Primary
              </>
            )}
          </Button>
        </div>
      )}

      {/* Content Section */}
      <div className="p-6 flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className={`mb-4 flex-shrink-0 ${!imageUrl && onSetPrimary && !isPrimary ? 'pr-36' : !imageUrl && isPrimary ? 'pr-24' : ''}`}>
          <h3 className="text-xl font-bold font-display text-charcoal mb-2 line-clamp-2">
            {name}
          </h3>
          {distance !== undefined && (
            <p className="text-sm text-charcoal-subtle">
              {distance.toFixed(1)} miles away
            </p>
          )}
        </div>

      {/* Main Content - Flexible */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Address */}
        {formatAddress() && (
          <div className="flex items-start gap-2 mb-3 text-charcoal-muted flex-shrink-0">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-charcoal-subtle" />
            <span className="text-sm">{formatAddress()}</span>
          </div>
        )}

        {/* Contact Information */}
        <div className="space-y-2 mb-4 flex-shrink-0">
          {phoneNumber && (
            <div className="flex items-center gap-2 text-charcoal-muted">
              <Phone className="w-4 h-4 flex-shrink-0 text-charcoal-subtle" />
              <span className="text-sm">{phoneNumber}</span>
            </div>
          )}
          {email && (
            <div className="flex items-center gap-2 text-charcoal-muted">
              <Mail className="w-4 h-4 flex-shrink-0 text-charcoal-subtle" />
              <span className="text-sm break-all">{email}</span>
            </div>
          )}
        </div>

        {/* Mass Times */}
        {hasMassTimes && (
          <div className="mt-4 pt-4 border-t border-surface-border flex-shrink-0">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-gold" />
              <h4 className="text-sm font-semibold text-charcoal">
                Mass Times
              </h4>
            </div>
            <div className="space-y-1">
              {massTimesContent}
            </div>
          </div>
        )}

        {/* Description - Takes remaining space */}
        {description && (
          <div className="mt-4 pt-4 border-t border-surface-border flex-1 min-h-0">
            <p className="text-sm text-charcoal-muted line-clamp-3">
              {description}
            </p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default ParishCard;
